// SNIGJ - Admin Translation Management Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Globe, Languages, Search, Plus, Edit, Trash2, Save, RefreshCw,
  Check, X, AlertCircle, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import translations, { AVAILABLE_LANGUAGES } from '../lib/i18n';

// Flatten nested object to dot notation
const flattenObject = (obj, prefix = '') => {
  return Object.keys(obj).reduce((acc, key) => {
    const pre = prefix.length ? prefix + '.' : '';
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(acc, flattenObject(obj[key], pre + key));
    } else {
      acc[pre + key] = obj[key];
    }
    return acc;
  }, {});
};

const AdminTranslationsPage = () => {
  const { language } = useAuth();
  const [customTranslations, setCustomTranslations] = useState([]);
  const [selectedLang, setSelectedLang] = useState('fr');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  
  // Form state
  const [newTranslation, setNewTranslation] = useState({
    language: 'fr',
    key: '',
    value: ''
  });

  // Get base translations flattened
  const baseTranslations = flattenObject(translations[selectedLang] || translations.fr);
  const baseKeys = Object.keys(baseTranslations);

  useEffect(() => {
    loadCustomTranslations();
  }, []);

  const loadCustomTranslations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/translations');
      setCustomTranslations(response.data.translations || []);
    } catch (error) {
      console.error('Failed to load translations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTranslation = async () => {
    try {
      await api.post('/admin/translations', newTranslation);
      toast.success(language === 'fr' ? 'Traduction enregistrée' : 'Translation saved');
      setShowAddModal(false);
      setNewTranslation({ language: selectedLang, key: '', value: '' });
      setEditingTranslation(null);
      loadCustomTranslations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteTranslation = async (translationId) => {
    if (!confirm(language === 'fr' ? 'Supprimer cette traduction personnalisée?' : 'Delete this custom translation?')) {
      return;
    }
    
    try {
      await api.delete(`/admin/translations/${translationId}`);
      toast.success(language === 'fr' ? 'Traduction supprimée' : 'Translation deleted');
      loadCustomTranslations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleEditTranslation = (tr) => {
    setNewTranslation({
      language: tr.language,
      key: tr.key,
      value: tr.value
    });
    setEditingTranslation(tr);
    setShowAddModal(true);
  };

  const getCustomValue = (key, lang) => {
    const custom = customTranslations.find(t => t.key === key && t.language === lang);
    return custom?.value;
  };

  const isCustomized = (key, lang) => {
    return customTranslations.some(t => t.key === key && t.language === lang);
  };

  // Filter translations based on search
  const filteredKeys = baseKeys.filter(key => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    const baseValue = baseTranslations[key]?.toLowerCase() || '';
    const customValue = getCustomValue(key, selectedLang)?.toLowerCase() || '';
    return key.toLowerCase().includes(lowerQuery) || 
           baseValue.includes(lowerQuery) || 
           customValue.includes(lowerQuery);
  });

  // Group keys by category
  const groupedKeys = {};
  filteredKeys.forEach(key => {
    const category = key.split('.')[0];
    if (!groupedKeys[category]) {
      groupedKeys[category] = [];
    }
    groupedKeys[category].push(key);
  });

  const categoryNames = {
    nav: language === 'fr' ? 'Navigation' : 'Navigation',
    auth: language === 'fr' ? 'Authentification' : 'Authentication',
    dashboard: language === 'fr' ? 'Tableau de bord' : 'Dashboard',
    cases: language === 'fr' ? 'Dossiers' : 'Cases',
    prisoners: language === 'fr' ? 'Détenus' : 'Prisoners',
    documents: language === 'fr' ? 'Documents' : 'Documents',
    payments: language === 'fr' ? 'Paiements' : 'Payments',
    border: language === 'fr' ? 'Contrôle frontalier' : 'Border Control',
    clemency: language === 'fr' ? 'Grâce' : 'Clemency',
    admin: language === 'fr' ? 'Administration' : 'Administration',
    audit: language === 'fr' ? 'Audit' : 'Audit',
    common: language === 'fr' ? 'Commun' : 'Common',
    classifications: language === 'fr' ? 'Classifications' : 'Classifications',
    roles: language === 'fr' ? 'Rôles' : 'Roles',
    citizen: language === 'fr' ? 'Citoyen' : 'Citizen'
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-translations-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
              <Languages className="w-8 h-8 text-primary" />
              {language === 'fr' ? 'Gestion des Traductions' : 'Translation Management'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Personnalisez les étiquettes et textes dans toutes les langues' 
                : 'Customize labels and texts in all languages'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadCustomTranslations} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              {language === 'fr' ? 'Actualiser' : 'Refresh'}
            </Button>
            <Button onClick={() => { setNewTranslation({ language: selectedLang, key: '', value: '' }); setEditingTranslation(null); setShowAddModal(true); }} className="gap-2">
              <Plus className="w-4 h-4" />
              {language === 'fr' ? 'Nouvelle traduction' : 'New Translation'}
            </Button>
          </div>
        </div>

        {/* Language Tabs & Search */}
        <div className="flex items-center justify-between gap-4">
          <Tabs value={selectedLang} onValueChange={setSelectedLang} className="flex-1">
            <TabsList>
              {AVAILABLE_LANGUAGES.map((lang) => (
                <TabsTrigger key={lang.code} value={lang.code} className="gap-2">
                  {lang.code === 'fr' && '🇫🇷'}
                  {lang.code === 'en' && '🇬🇧'}
                  {['ln', 'sw', 'kg', 'lu'].includes(lang.code) && '🇨🇩'}
                  {lang.native}
                  {customTranslations.filter(t => t.language === lang.code).length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {customTranslations.filter(t => t.language === lang.code).length}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Custom Translations Summary */}
        {customTranslations.filter(t => t.language === selectedLang).length > 0 && (
          <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-900 dark:text-green-100">
                    {customTranslations.filter(t => t.language === selectedLang).length}{' '}
                    {language === 'fr' ? 'traduction(s) personnalisée(s)' : 'custom translation(s)'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Translation Categories */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => <div key={i} className="h-40 skeleton" />)}
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedKeys).map((category) => (
              <Card key={category}>
                <CardHeader className="py-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {categoryNames[category] || category}
                    <Badge variant="outline" className="ml-2">
                      {groupedKeys[category].length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {groupedKeys[category].map((key) => {
                      const baseValue = baseTranslations[key];
                      const customValue = getCustomValue(key, selectedLang);
                      const hasCustom = isCustomized(key, selectedLang);
                      
                      return (
                        <div 
                          key={key} 
                          className={`p-3 rounded border flex items-center gap-4 ${
                            hasCustom ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200' : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-mono text-muted-foreground mb-1">{key}</p>
                            <div className="flex items-center gap-2">
                              {hasCustom ? (
                                <>
                                  <span className="text-muted-foreground line-through text-sm">{baseValue}</span>
                                  <ArrowRight className="w-4 h-4 text-yellow-600" />
                                  <span className="font-medium text-yellow-700 dark:text-yellow-400">{customValue}</span>
                                </>
                              ) : (
                                <span>{baseValue}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditTranslation({
                                translation_id: `tr_${selectedLang}_${key.replace(/\./g, '_')}`,
                                language: selectedLang,
                                key,
                                value: customValue || baseValue
                              })}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {hasCustom && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTranslation(`tr_${selectedLang}_${key.replace(/\./g, '_')}`)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingTranslation 
                  ? (language === 'fr' ? 'Modifier la traduction' : 'Edit Translation')
                  : (language === 'fr' ? 'Nouvelle traduction' : 'New Translation')}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? 'Personnalisez le texte affiché pour cette clé'
                  : 'Customize the displayed text for this key'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Langue' : 'Language'}</Label>
                <Select 
                  value={newTranslation.language} 
                  onValueChange={(v) => setNewTranslation({...newTranslation, language: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.native}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Clé de traduction' : 'Translation Key'}</Label>
                <Select 
                  value={newTranslation.key} 
                  onValueChange={(v) => setNewTranslation({...newTranslation, key: v, value: baseTranslations[v] || ''})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'fr' ? 'Sélectionner une clé...' : 'Select a key...'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {baseKeys.map((key) => (
                      <SelectItem key={key} value={key}>
                        <span className="font-mono text-xs">{key}</span>
                        <span className="ml-2 text-muted-foreground">({baseTranslations[key]?.substring(0, 30)}...)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {newTranslation.key && (
                <div className="p-3 bg-muted rounded">
                  <p className="text-xs text-muted-foreground mb-1">
                    {language === 'fr' ? 'Valeur par défaut:' : 'Default value:'}
                  </p>
                  <p className="text-sm">{baseTranslations[newTranslation.key]}</p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nouvelle valeur' : 'New Value'}</Label>
                <Textarea 
                  value={newTranslation.value}
                  onChange={(e) => setNewTranslation({...newTranslation, value: e.target.value})}
                  rows={3}
                  placeholder={language === 'fr' ? 'Entrez la nouvelle traduction...' : 'Enter new translation...'}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
                <Button 
                  onClick={handleSaveTranslation}
                  disabled={!newTranslation.key || !newTranslation.value}
                  className="gap-2"
                >
                  <Save className="w-4 h-4" />
                  {language === 'fr' ? 'Enregistrer' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Missing import
const ArrowRight = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
  </svg>
);

export default AdminTranslationsPage;
